import { AdminPanelTopic, Scraper, ScraperConfig, ScraperConfigOptionValue } from "../../components/TopicsPanel/types";
import { deleteOptionValue, deleteScraperConfig } from "../../services/delete";
import { updateActiveFlag, updateOptionValue, updateScraperConfig } from "../../services/patch";
import { createConfig, createConfigOptionValue } from "../../services/post";
import { CreateOrUpdateConfigBody, CreateOrUpdateOptionValue, EndpointsWithActiveFlag } from "../../services/types";


export const useScraperHandlers = (
    topics: AdminPanelTopic[],
    setTopics: React.Dispatch<React.SetStateAction<AdminPanelTopic[]>>,
    selectedTopic: number
  ) => {

    const addConfig = (newConfig: ScraperConfig) => {
        setTopics((prevTopics) => {
          if (!prevTopics || selectedTopic === null) {
            return prevTopics;
          }
          return prevTopics.map((topic) => {
            if (topic.id === selectedTopic) {
              return {...topic, scraperConfigs: [newConfig, ...topic.scraperConfigs]}
            }
            return topic;
          });
        });
      };

      const updateConfigs = (updatedConfig: ScraperConfig) => {
        setTopics((prevTopics) => {
          if (!prevTopics || selectedTopic === null) {
            return prevTopics;
          }
          return prevTopics.map((topic) => {
            if (topic.id === selectedTopic) {
              return {...topic, scraperConfigs: topic.scraperConfigs.map(config => {
                if (config.id === updatedConfig.id) {
                    return updatedConfig
                }
                return config
              })}
            }
            return topic;
          });
        });
      };

      const deleteConfig = (configId: number) => {
        setTopics((prevTopics) => {
          if (!prevTopics || selectedTopic === null) {
            return prevTopics;
          }
          return prevTopics.map((topic) => {
            if (topic.id === selectedTopic) {
              return {...topic, scraperConfigs: [...topic.scraperConfigs.filter(c => c.id !== configId)]}
            }
            return topic;
          });
        });
      };

      const getScraperConfigOptions = (topic, configId, configOptionName, modifyOptionValues) => {
        if (topic.id === selectedTopic) {
          return {
            ...topic,
            scraperConfigs: topic.scraperConfigs.map((config) => {
              if (config.id === configId) {
                return {
                  ...config,
                  scraperConfigOptions: config.scraperConfigOptions.map((configOption) => {
                    if (configOption.name === configOptionName) {
                      return modifyOptionValues(configOption);
                    }
                    return configOption;
                  }),
                };
              }
              return config;
            }),
          };
        }
        return topic;
      };

      const addConfigOptionValues = (configId: number, configOptionName: string, optionValue: ScraperConfigOptionValue) => {
        setTopics((prevTopics) => {
            if (!prevTopics || selectedTopic === null) {
              return prevTopics;
            }
            return prevTopics.map((topic) =>
            getScraperConfigOptions(topic, configId, configOptionName, (configOption) => ({
                ...configOption,
                scraperConfigOptionValues: [
                  optionValue,
                  ...configOption.scraperConfigOptionValues,
                ],
              }))
            );
          });
      };

      const updateConfigOptionValues = (configId: number, configOptionName: string, optionValue: ScraperConfigOptionValue) => {
        setTopics((prevTopics) => {
            if (!prevTopics || selectedTopic === null) {
              return prevTopics;
            }
            return prevTopics.map((topic) =>
            getScraperConfigOptions(topic, configId, configOptionName, (configOption) => ({
                ...configOption,
                scraperConfigOptionValues: [
                    ...configOption.scraperConfigOptionValues.map(v => v.id === optionValue.id ? optionValue : v)
                ],
              }))
            );
          });
      };

      const deleteConfigOptionValue = (configId: number, configOptionName: string, optionValueId: number) => {
        setTopics((prevTopics) => {
            if (!prevTopics || selectedTopic === null) {
              return prevTopics;
            }
            return prevTopics.map((topic) =>
              getScraperConfigOptions(topic, configId, configOptionName, (configOption) => ({
                ...configOption,
                scraperConfigOptionValues: configOption.scraperConfigOptionValues.filter(
                  (o) => o.id !== optionValueId
                ),
              }))
            );
          });
      };

      const handleScrapers = {
        add: async (scraper: Scraper): Promise<void> => {},
      };
    
      const handleConfigs = {
        add: async (config: ScraperConfig, save = true): Promise<void> => {
          if (!save) return addConfig({...config, topicId: selectedTopic});
          const createConfigBody: CreateOrUpdateConfigBody = {name: config.name, scraperId: config.scraper.id, topicId: selectedTopic, active: config.active}
          const response = await createConfig(createConfigBody)
          const savedId = response.result
          deleteConfig(config.id)
          addConfig({...config, id: savedId})
        },
        update: async (config: ScraperConfig, updateActive = false): Promise<void> => {
          let response;
          if (updateActive) {
            response = await updateActiveFlag(EndpointsWithActiveFlag.CONFIG, config.id, config.active)
          } else {
            const updateConfigBody = { name: config.name, scraperId: config.scraper.id, topicId: selectedTopic, active: config.active };
            response = await updateScraperConfig(config.id, updateConfigBody);
          }
          if (!response?.isSuccess) return
          updateConfigs(config);
      },
        delete: async (configId: number): Promise<void> => {
          if (configId < 0) return deleteConfig(configId);
            await deleteScraperConfig(configId);
            deleteConfig(configId);
        }
      };
    
      const handleConfigOptionValues = {
        add: async (configId: number, configOptionName: string, optionValue: ScraperConfigOptionValue, save = true): Promise<void> => {
            if (!save) {
                addConfigOptionValues(configId, configOptionName, optionValue)
                return;
            }
            const createOptionValue: CreateOrUpdateOptionValue = {
              value: optionValue.value, active: optionValue.active, configId: configId, configOptionName: configOptionName
            }
            const response = await createConfigOptionValue(createOptionValue)
            const savedId = response.result
            deleteConfigOptionValue(configId, configOptionName, optionValue.id)
            addConfigOptionValues(configId, configOptionName, {...optionValue, id: savedId})
        },
        addAll: async (configId: number, configOptionName: string): Promise<void> => {
            // const unsavedValues = topics.find(t => t.id === selectedTopic).scraperConfigs.find(c => c.id === configId).scraperConfigOptions.find(o => o.name === configOptionName).scraperConfigOptionValues.filter(v => v.id < 0)
            // let newId = Math.max(...topics.find(t => t.id === selectedTopic).scraperConfigs.find(c => c.id === configId).scraperConfigOptions.find(o => o.name === configOptionName).scraperConfigOptionValues.map(v => v.id)) + 1
            // unsavedValues.forEach(v => {
            //     const unsavedItemWithId = {id: newId, value: v.value}
            //     deleteConfigOptionValue(configId, configOptionName, v.id)
            //     addConfigOptionValues(configId, configOptionName, unsavedItemWithId)
            //     newId = newId + 1
            //  })
        },
        update: async (configId: number, configOptionName: string, optionValue: ScraperConfigOptionValue, updateActive = false): Promise<void> => {
            if (!configId && !configOptionName && !optionValue?.id && optionValue.id < 0){
                throw new Error(`
                Failed to update configOptionValue with id: ${optionValue.id} value: ${optionValue.value}, due to invalid params.
                id: ${optionValue.id}, value: ${optionValue.value}, configId: ${configId}, configOptionName: ${configOptionName}
                `);
            }
            if (updateActive) {
              await updateActiveFlag(EndpointsWithActiveFlag.CONFIG_OPTION_VALUE, optionValue.id, optionValue.active)
            } else {
              await updateOptionValue(optionValue.id, {
                configId: configId, active: optionValue.active, configOptionName: configOptionName, value: optionValue.value
              })
            }
            updateConfigOptionValues(configId, configOptionName, optionValue)
            
        },
        delete: async (configId: number, configOptionName: string, optionValueId: number): Promise<void> => {
            if (optionValueId > 0) await deleteOptionValue(optionValueId)
            deleteConfigOptionValue(configId, configOptionName, optionValueId)
        },
      };

      return { handleScrapers, handleConfigs, handleConfigOptionValues };
  }